
import { CallbackRegistry, UnsubscribeFunction } from "callback-registry";
import { Glue42Core } from "@glue42/core";
import { Glue42Office } from "../../glue4office";
import { CloseMethodName, EditHtmlMethodName, ReceiveHtmlMethodName, ChunkSize, OnReturnHtmlMethodName } from "./const";
import documentFactory from "./document";
import {
    HtmlReceiveInvocationResult, InternalDocumentApi, InternalOpenDocumentConfig,
    OnCloseInvocationResult,
    Config,
} from "./types";
import { mapOptions } from "./utils";
const shortid = require("shortid");

const CallbackFactory = require("callback-registry");

export default (config: Config): Glue42Office.Word.API => {
    const agm: Glue42Core.AGM.API = config.agm;
    const _registry: CallbackRegistry = CallbackFactory();

    let connected: boolean = false;
    const documents: any = {};

    function openDocument(options: Glue42Office.Word.OpenDocumentConfig): Promise<Glue42Office.Word.DocumentApi> {
        return new Promise((resolve, reject) => {
            const internalConfig: InternalOpenDocumentConfig = mapOptions(options);
            internalConfig.cookie = (options.name ?? "unnamed") + "_" + shortid.generate();
            const successHandler = (args: any): void => {
                // internalConfig.cookie = args.returned.sessionId;
                // Create a new document object and resolve the Promise
                const doc: InternalDocumentApi = documentFactory(internalConfig.cookie, options, agm);
                documents[internalConfig.cookie] = doc;
                resolve(doc.documentApi);
            };

            const errorHandler = (e: object): void => {
                // tslint:disable-next-line:no-console
                console.warn(e);
                reject(e);
            };

            let offset = 0;
            let chunks = 0;
            const chunkedData: any = [];

            while (offset < options.data.length) {
                chunkedData.push(options.data.substr(offset, ChunkSize));
                offset += ChunkSize;
            }

            function sendChunks(args?: any) {
                const chunk = chunkedData.splice(0, 1);
                if (chunk.length === 0) {
                    successHandler(args);
                    return;
                }

                internalConfig.sendDocx = true;

                if (options.isDocx) {
                    internalConfig.docx = chunk[0];
                    internalConfig.lengthDocx = chunk[0].length;
                    internalConfig.totalLengthDocx = options.data.length;
                    internalConfig.offsetDocx = chunks * ChunkSize;
                } else {
                    internalConfig.html = chunk[0];
                    internalConfig.length = chunk[0].length;
                    internalConfig.totalLength = options.data.length;
                    internalConfig.offset = chunks * ChunkSize;
                }
                invokeEditHtmlMethod(internalConfig).then(sendChunks).catch(errorHandler);
                chunks++;
            }
            sendChunks();
        });
    }

    // #region "Internal functions"
    agm.methodAdded((method) => {
        if (method.name === EditHtmlMethodName) {
            if (connected === false) {
                connected = true;
                _registry.execute("onStatusChanged", { connected });
            }
        }
    });

    agm.methodRemoved((method) => {
        if (method.name === EditHtmlMethodName) {
            connected = false;
            _registry.execute("onStatusChanged", { connected });
        }
    });

    // OnDocumentChanged
    agm.register(ReceiveHtmlMethodName, (args: HtmlReceiveInvocationResult, caller: Glue42Core.AGM.Instance) => {
        const document: InternalDocumentApi = documents[args.cookie];
        if (document) {
            document.documentEvents._onChanged(args);
        }

        return undefined;
    });

    agm.register(OnReturnHtmlMethodName, (args: HtmlReceiveInvocationResult, caller: Glue42Core.AGM.Instance) => {
        const document: InternalDocumentApi = documents[args.cookie];
        if (document) {
            document.documentEvents._onHtml(args);
        }
        return undefined;
    });

    // Closing method
    agm.register({
        name: CloseMethodName,
        accepts: "String documentName, String cookie",
    }, (args: OnCloseInvocationResult, caller: Glue42Core.AGM.Instance) => {
        // Get cookie and delete the document and execute callback onClosed
        const document: InternalDocumentApi = documents[args.cookie];
        if (document) {
            document.documentEvents._close();
        }

        delete documents[args.cookie];

        return undefined;
    });

    function invokeEditHtmlMethod(options: InternalOpenDocumentConfig): Promise<object> {
        return new Promise((resolve, reject) => {
            if (!connected) {
                reject("Microsoft Word with Tick42 Add-in is not running");
            } else {
                agm.invoke(EditHtmlMethodName, options).then(resolve).catch(reject);
            }
        });
    }
    // #endregion "Internal functions"

    function ready(): Promise<object> {
        return new Promise((resolve, reject) => {
            resolve(api);
        });
    }

    function onAddinStatusChanged(callback: (args: { connected: boolean }) => void): UnsubscribeFunction {
        if (agm.methods({ name: EditHtmlMethodName }).length > 0) {
            connected = true;
        }

        callback({ connected });
        return _registry.add("onStatusChanged", callback);
    }

    const api: Glue42Office.Word.API = {
        get all(): Glue42Office.Word.DocumentApi[] {
            return Object.keys(documents).map((k) => {
                return documents[k].documentApi;
            });
        },
        openDocument,
        ready,
        onAddinStatusChanged,
        get addinStatus() {
            return connected;
        },
    };

    return api;
};
